import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  ClipboardList,
  HeartHandshake,
  MapPin,
} from "lucide-react";
import {
  subscribeToHousingDevelopmentEvents,
  subscribeToHousingDevelopmentSurveys,
} from "./housingDevelopmentService";
import {
  formatHousingDevelopmentEventAddress,
  formatHousingDevelopmentEventDate,
  formatHousingDevelopmentEventTime,
  housingDevelopmentEventPath,
  housingDevelopmentPartners,
  housingDevelopmentSurveyPath,
  partitionHousingDevelopmentEvents,
  publicHousingDevelopmentEventSurveyLinks,
} from "./housingDevelopmentUtils";
import "./HousingDevelopment.css";

function CollectionState({ loading, error, retry, empty, children }) {
  if (loading) return <p role="status" className="hd-status">Loading…</p>;
  if (error) {
    return (
      <div role="alert" className="hd-alert">
        <p>{error}</p>
        <button type="button" className="hd-button hd-button--quiet" onClick={retry}>
          Retry
        </button>
      </div>
    );
  }
  if (empty) return <p className="hd-empty">Nothing has been published here yet.</p>;
  return children;
}

function EventCard({ event, past = false, surveys = [] }) {
  const eventPath = housingDevelopmentEventPath(event);
  const surveyLinks = publicHousingDevelopmentEventSurveyLinks(event, surveys);
  const address = formatHousingDevelopmentEventAddress(event, {
    includeVenue: true,
  });

  return (
    <article className="hd-card hd-event-card">
      <p className="hd-eyebrow">{past ? "Past event" : "Housing & Development event"}</p>
      <h3>{event.title || event.name || "Event"}</h3>
      {event.subtitle ? <p className="hd-card__subtitle">{event.subtitle}</p> : null}
      <dl className="hd-facts">
        <div>
          <dt><CalendarDays aria-hidden="true" /> Date and time</dt>
          <dd>
            {formatHousingDevelopmentEventDate(event)}, {formatHousingDevelopmentEventTime(event)}
          </dd>
        </div>
        {address ? (
          <div>
            <dt><MapPin aria-hidden="true" /> Location</dt>
            <dd>{address}</dd>
          </div>
        ) : null}
      </dl>
      <div className="hd-card__actions">
        <Link className="hd-button" to={`${eventPath}?source=website`}>
          View event
        </Link>
        {!past ? surveyLinks.map((survey, index) => (
          <Link
            key={survey.path}
            className="hd-button hd-button--survey"
            to={`${survey.path}?source=website`}
          >
            {survey.title || (surveyLinks.length === 1
              ? "Complete event survey"
              : `Complete survey ${index + 1}`)}
          </Link>
        )) : null}
      </div>
    </article>
  );
}

function SurveyCard({ survey }) {
  const partners = Array.isArray(survey.partnerOrganisations)
    ? survey.partnerOrganisations.filter(Boolean)
    : [];
  return (
    <article className="hd-card hd-survey-card">
      <ClipboardList aria-hidden="true" className="hd-card__icon" />
      <p className="hd-eyebrow">Current survey</p>
      <h3>{survey.title || survey.surveyName || "Community survey"}</h3>
      {survey.description ? <p>{survey.description}</p> : null}
      {partners.length ? (
        <p className="hd-card__meta">With {partners.join(", ")}</p>
      ) : null}
      {survey.closeDate ? (
        <p className="hd-card__meta">Closes {survey.closeDate}</p>
      ) : null}
      <Link
        className="hd-button hd-button--survey"
        to={`${housingDevelopmentSurveyPath(survey)}?source=website`}
      >
        Complete survey
      </Link>
    </article>
  );
}

export default function HousingDevelopmentHub({ now = null }) {
  const [events, setEvents] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");
  const [surveysError, setSurveysError] = useState("");
  const [eventsAttempt, setEventsAttempt] = useState(0);
  const [surveysAttempt, setSurveysAttempt] = useState(0);
  const [clock, setClock] = useState(() => now || new Date());

  useEffect(() => {
    if (now) {
      setClock(now);
      return undefined;
    }
    const timer = window.setInterval(() => setClock(new Date()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, [now]);

  useEffect(() => {
    setEventsLoading(true);
    setEventsError("");
    return subscribeToHousingDevelopmentEvents(
      (records) => {
        setEvents(records);
        setEventsLoading(false);
      },
      (error) => {
        console.error("Unable to load Housing & Development events", error);
        setEventsError("Housing & Development events are temporarily unavailable.");
        setEventsLoading(false);
      }
    );
  }, [eventsAttempt]);

  useEffect(() => {
    setSurveysLoading(true);
    setSurveysError("");
    return subscribeToHousingDevelopmentSurveys(
      (records) => {
        setSurveys(records);
        setSurveysLoading(false);
      },
      (error) => {
        console.error("Unable to load Housing & Development surveys", error);
        setSurveysError("Current surveys are temporarily unavailable.");
        setSurveysLoading(false);
      }
    );
  }, [surveysAttempt]);

  const { upcomingEvents, pastEvents } = useMemo(
    () => partitionHousingDevelopmentEvents(events, clock),
    [clock, events]
  );
  const visibleSurveys = useMemo(
    () => surveys.filter((survey) => survey.publicVisibility !== false),
    [surveys]
  );
  const partners = useMemo(
    () => housingDevelopmentPartners(events, visibleSurveys),
    [events, visibleSurveys]
  );

  return (
    <main className="hd-page">
      <section className="hd-hero">
        <div className="hd-shell hd-hero__content">
          <p className="hd-kicker">Urban Unity Network</p>
          <h1>Housing &amp; Development</h1>
          <p className="hd-hero__strapline">
            Connecting residents, housing providers and local opportunity.
          </p>
          <p className="hd-hero__intro">
            UUN brings residents, housing associations, developers, contractors and
            community partners together around communication, local priorities,
            careers, skills and social-value opportunities.
          </p>
          <div className="hd-hero__links">
            <a className="hd-button" href="#upcoming-events">View upcoming events</a>
            <a className="hd-button hd-button--survey" href="#current-surveys">Current surveys</a>
          </div>
        </div>
      </section>

      <section id="upcoming-events" className="hd-section hd-shell" aria-labelledby="hd-upcoming-title">
        <div className="hd-section__heading">
          <CalendarDays aria-hidden="true" />
          <div>
            <p className="hd-eyebrow">Take part</p>
            <h2 id="hd-upcoming-title">Upcoming events</h2>
          </div>
        </div>
        <CollectionState
          loading={eventsLoading}
          error={eventsError}
          retry={() => setEventsAttempt((value) => value + 1)}
          empty={upcomingEvents.length === 0}
        >
          <div className="hd-grid hd-grid--events">
            {upcomingEvents.map((event) => <EventCard key={event.id} event={event} surveys={surveys} />)}
          </div>
        </CollectionState>
      </section>

      <section id="current-surveys" className="hd-section hd-section--tint" aria-labelledby="hd-surveys-title">
        <div className="hd-shell">
          <div className="hd-section__heading">
            <ClipboardList aria-hidden="true" />
            <div>
              <p className="hd-eyebrow">Share your experience</p>
              <h2 id="hd-surveys-title">Current surveys</h2>
            </div>
          </div>
          <CollectionState
            loading={surveysLoading}
            error={surveysError}
            retry={() => setSurveysAttempt((value) => value + 1)}
            empty={visibleSurveys.length === 0}
          >
            <div className="hd-grid">
              {visibleSurveys.map((survey) => <SurveyCard key={survey.id} survey={survey} />)}
            </div>
          </CollectionState>
        </div>
      </section>

      <section className="hd-section hd-shell" aria-labelledby="hd-work-title">
        <div className="hd-section__heading">
          <Building2 aria-hidden="true" />
          <div>
            <p className="hd-eyebrow">Our role</p>
            <h2 id="hd-work-title">Our Housing &amp; Development work</h2>
          </div>
        </div>
        <div className="hd-grid hd-grid--work">
          {[
            ["Resident engagement", "Gather resident feedback, identify local concerns and support clear communication with housing providers."],
            ["Consultation and evidence", "Support consultation, collect evidence and help partners understand local priorities."],
            ["Careers and skills", "Promote employment, training, careers events and social-value opportunities for local people."],
          ].map(([title, copy]) => (
            <article className="hd-card" key={title}>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="hd-section hd-section--tint" aria-labelledby="hd-past-title">
        <div className="hd-shell">
          <div className="hd-section__heading">
            <CalendarDays aria-hidden="true" />
            <div>
              <p className="hd-eyebrow">Activity archive</p>
              <h2 id="hd-past-title">Past events</h2>
            </div>
          </div>
          <CollectionState
            loading={eventsLoading}
            error={eventsError}
            retry={() => setEventsAttempt((value) => value + 1)}
            empty={pastEvents.length === 0}
          >
            <div className="hd-grid hd-grid--events">
              {pastEvents.map((event) => <EventCard key={event.id} event={event} surveys={surveys} past />)}
            </div>
          </CollectionState>
        </div>
      </section>

      <section className="hd-section hd-shell hd-split" aria-label="Programme information">
        <article className="hd-panel">
          <ClipboardList aria-hidden="true" />
          <h2>Survey &amp; community insights</h2>
          <p>
            Surveys help UUN and participating organisations understand resident
            priorities and the support local people are looking for. Response data
            remains private unless UUN deliberately publishes an approved summary.
          </p>
        </article>
        <article className="hd-panel">
          <HeartHandshake aria-hidden="true" />
          <h2>Partners / collaborations</h2>
          {partners.length ? (
            <ul className="hd-partner-list">
              {partners.map((partner) => <li key={partner}>{partner}</li>)}
            </ul>
          ) : (
            <p>Collaborating organisations will be listed with their published events and surveys.</p>
          )}
        </article>
      </section>

      <section className="hd-contact" aria-labelledby="hd-contact-title">
        <div className="hd-shell hd-contact__content">
          <div>
            <p className="hd-eyebrow">Start a conversation</p>
            <h2 id="hd-contact-title">Contact UUN</h2>
            <p>
              Talk to Urban Unity Network about resident engagement, a local event,
              a survey or a Housing &amp; Development collaboration.
            </p>
          </div>
          <a className="hd-button" href="mailto:info@urbanunitynetwork.com">
            info@urbanunitynetwork.com
          </a>
        </div>
      </section>
    </main>
  );
}

